<main>
  <loading-overlay [isLoading]="isLoading() > 0" [fullWidth]="true" />
  <p-toast position="bottom-right" key="br" />

  <header>
    <div class="headline">
      <h1>{{ headerTitle() }}</h1>
      <p-tag [severity]="statusSeverity(status())" [value]="statusLabel(status())" />
    </div>
    <div class="buttons">
      @if (canDelete()) {
        <p-button [label]="'Delete' | translateWF: 'Delete'" icon="pi pi-trash" severity="danger" variant="outlined" (onClick)="confirmDelete()" />
      }
      @if (canDecide()) {
        <p-button [label]="'Trip_Reject' | translateWF: 'Reject'" icon="pi pi-times" severity="danger" variant="outlined" (onClick)="openReject()" />
        <p-button [label]="'Trip_Approve' | translateWF: 'Approve'" icon="pi pi-check" severity="success" (onClick)="openApprove()" />
      }
      @if (isOpen()) {
        <p-button [label]="'Receipt_Title' | translateWF: 'Capture receipts'" icon="pi pi-receipt" variant="outlined" (onClick)="captureReceipts()" />
      }
      @if (isOpen() && canClaim()) {
        <p-button [label]="'ExpenseClaim_New' | translateWF: 'New expense claim'" icon="pi pi-file-check" variant="outlined" (onClick)="newClaim()" />
      }
      @if (isOpen() && canTreasury) {
        <p-button [label]="'EmployeeAdvance_New' | translateWF: 'New advance'" icon="pi pi-money-bill" variant="outlined" (onClick)="newAdvance()" />
      }
      @if (canSettle()) {
        <p-button [label]="'ExpenseSettlement_Settle' | translateWF: 'Settle'" icon="pi pi-calculator" variant="outlined" (onClick)="settle()" />
      }
      @if (canSubmit()) {
        <p-button [label]="'Trip_Submit' | translateWF: 'Send for approval'" icon="pi pi-send" variant="outlined" (onClick)="submit()" />
      }
      @if (hasExtraButtons() && isEditable()) {
        <span class="separator"></span>
      }
      @if (isEditable()) {
        <p-splitbutton [label]="'Save' | translateWF: 'Save'" (onClick)="save(false)" [model]="saveMenuItems" />
      }
      <p-button variant="outlined" (onClick)="back()">{{ 'Back' | translateWF: 'Back' }}</p-button>
    </div>
  </header>

  <div class="content">
    <p-confirmDialog />

    <div class="flex flex-wrap items-center gap-3 mb-5">
      @for (step of steps(); track step.status; let i = $index) {
        <div class="flex items-center gap-2">
          <span
            class="inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-semibold"
            [ngClass]="
              step.current
                ? 'bg-[var(--p-primary-color)] text-white'
                : step.done
                  ? 'bg-[var(--p-primary-100)] text-[var(--p-primary-700)]'
                  : 'bg-[var(--p-surface-200)] text-[var(--p-surface-600)]'
            "
          >
            @if (step.done) {
              <i class="pi pi-check text-xs" aria-hidden="true"></i>
            } @else {
              {{ i + 1 }}
            }
          </span>
          <span [ngClass]="step.current ? 'font-semibold' : 'text-surface-500'">{{ step.label }}</span>
        </div>
        @if (!$last) {
          <i class="pi pi-angle-right text-surface-400" aria-hidden="true"></i>
        }
      }
    </div>

    @if (ready()) {
      <div class="grid grid-cols-12 gap-5">
        <div class="col-span-12 xl:col-span-8">
          <p-panel [header]="'Trip_Section_Data' | translateWF: 'Trip data'">
            <p-card>
              <form [formGroup]="form" class="grid grid-cols-12 gap-5">
                <div class="form-box col-span-12 md:col-span-6">
                  <label for="trip-employee">{{ fieldLabels.employee | translateWF: 'Employee' }} *</label>
                  <p-autocomplete
                    inputId="trip-employee"
                    formControlName="employee"
                    [suggestions]="employeeSuggestions()"
                    (completeMethod)="searchEmployees($event)"
                    optionLabel="name"
                    [dropdown]="true"
                    [forceSelection]="true"
                    [fluid]="true"
                    [placeholder]="'Trip_Employee_Placeholder' | translateWF: 'Search an employee'"
                  >
                    <ng-template #item let-employee>
                      <div class="flex flex-col">
                        <span>{{ employee.name }}</span>
                        <small class="text-surface-500 font-mono">{{ employee.code }}</small>
                      </div>
                    </ng-template>
                  </p-autocomplete>
                  <small class="text-surface-500">
                    {{ 'Trip_Employee_Hint' | translateWF: 'Only employees enabled on the Employees page (active accounting).' }}
                  </small>
                </div>
                <div class="form-box col-span-12 md:col-span-6">
                  <label for="trip-destination">{{ fieldLabels.destination | translateWF: 'Destination' }} *</label>
                  <input
                    id="trip-destination"
                    pInputText
                    class="w-full"
                    formControlName="destination"
                    maxlength="120"
                    [placeholder]="'Trip_Destination_Placeholder' | translateWF: 'e.g. Ciudad de Guatemala, GT'"
                  />
                </div>

                <div class="form-box col-span-12 md:col-span-6">
                  <label for="trip-cost-center">{{ fieldLabels.costCenterReference | translateWF: 'Cost center' }} *</label>
                  <app-cost-center-select
                    inputId="trip-cost-center"
                    [control]="form.controls.costCenterReference"
                    [current]="costCenterCurrent()"
                    [readonly]="!isEditable()"
                    [placeholder]="'Trip_CostCenter_Placeholder' | translateWF: 'Select a cost center'"
                  />
                </div>
                <div class="form-box col-span-12 md:col-span-3">
                  <label for="trip-departure">{{ fieldLabels.departureDate | translateWF: 'Departure date' }} *</label>
                  <p-datepicker inputId="trip-departure" formControlName="departureDate" [showIcon]="true" appendTo="body" dateFormat="yy-mm-dd" styleClass="w-full" />
                </div>
                <div class="form-box col-span-12 md:col-span-3">
                  <label for="trip-return">{{ fieldLabels.returnDate | translateWF: 'Return date' }} *</label>
                  <p-datepicker
                    inputId="trip-return"
                    formControlName="returnDate"
                    [showIcon]="true"
                    appendTo="body"
                    dateFormat="yy-mm-dd"
                    [minDate]="form.controls.departureDate.value"
                    styleClass="w-full"
                  />
                </div>

                <div class="form-box col-span-12">
                  <label for="trip-policy">{{ fieldLabels.claimPolicyReference | translateWF: 'Expense claim policy' }}</label>
                  <p-select
                    inputId="trip-policy"
                    formControlName="claimPolicyReference"
                    [options]="policies()"
                    optionValue="id"
                    [showClear]="true"
                    [placeholder]="'PettyCashFund_Policy_Default' | translateWF: 'Default claim policy'"
                    styleClass="w-full"
                  >
                    <ng-template #selectedItem let-policy>{{ policyName(policy) }}</ng-template>
                    <ng-template #item let-policy>{{ policyName(policy) }}</ng-template>
                  </p-select>
                  <small class="text-surface-500">
                    {{
                      'Trip_Policy_Hint'
                        | translateWF: 'Approves this trip’s expense claims. Empty uses the default expense claim policy. The trip itself is approved by the Trip policy.'
                    }}
                  </small>
                </div>

                <div class="form-box col-span-12">
                  <label for="trip-purpose">{{ fieldLabels.purpose | translateWF: 'Purpose' }} *</label>
                  <textarea id="trip-purpose" pTextarea class="w-full" rows="3" formControlName="purpose" maxlength="500"></textarea>
                </div>
              </form>
            </p-card>
          </p-panel>
        </div>

        <div class="col-span-12 xl:col-span-4 flex flex-col gap-5">
          @if (trip()?.approval; as approval) {
            <p-panel [header]="'Trip_Section_Approval' | translateWF: 'Trip approval'">
              <p-card>
                <div class="flex flex-col gap-3">
                  <span class="text-sm text-surface-500">{{ 'Trip_Approval_Policy' | translateWF: 'Policy' }}: {{ policyName({ name: approval.policyName }) }}</span>
                  @if (approval.isAutoApproved) {
                    <p-message severity="info" [text]="'Trip_Approval_Auto' | translateWF: 'No approver applied: approved by the system.'" />
                  }
                  @for (view of slotViews(); track view.slot.id) {
                    <div class="flex items-start justify-between gap-3 py-1">
                      <span class="flex items-center gap-2">
                        <i [class]="slotIcon(view.slot)" aria-hidden="true"></i>
                        <span class="flex flex-col">
                          <span>{{ view.slot.value }}</span>
                          @if (view.by) {
                            <small class="text-surface-500">{{ view.by }} · {{ view.at | date: 'yyyy-MM-dd HH:mm' }}</small>
                          }
                          @if (view.comment) {
                            <small class="text-surface-500 italic">“{{ view.comment }}”</small>
                          }
                        </span>
                      </span>
                      @switch (view.state) {
                        @case ('approved') {
                          <p-tag severity="success" [value]="'Trip_Slot_Approved' | translateWF: 'Approved'" />
                        }
                        @case ('rejected') {
                          <p-tag severity="danger" [value]="'Trip_Slot_Rejected' | translateWF: 'Rejected'" />
                        }
                        @default {
                          <p-tag
                            severity="secondary"
                            [value]="view.slot.isRequired ? ('Trip_Slot_Pending' | translateWF: 'Pending') : ('Trip_Slot_Optional' | translateWF: 'Optional')"
                          />
                        }
                      }
                    </div>
                  }
                </div>
              </p-card>
            </p-panel>
          } @else {
            <p-panel [header]="'Trip_Section_Next' | translateWF: 'What happens next'">
              <p-card>
                <ol class="flex flex-col gap-2 list-decimal pl-5 text-sm">
                  <li>{{ 'Trip_Next_1' | translateWF: 'The trip goes to approval under the Trip policy.' }}</li>
                  <li>{{ 'Trip_Next_2' | translateWF: 'Once approved it is Open: request one or more advances and capture receipts.' }}</li>
                  <li>{{ 'Trip_Next_3' | translateWF: 'Without an advance the employee pays and is reimbursed in the settlement.' }}</li>
                  <li>{{ 'Trip_Next_4' | translateWF: 'On return, build the expense claim from the trip’s receipts.' }}</li>
                </ol>
                <small class="block mt-3 text-surface-500">
                  {{ 'Trip_Next_NoPosting' | translateWF: 'Creating a trip posts nothing. Money only moves with an advance or in the settlement.' }}
                </small>
              </p-card>
            </p-panel>
          }

          @if (!isNew() && (isOpen() || status() === tripStatuses.InSettlement || status() === tripStatuses.Closed)) {
            <p-panel [header]="'EmployeeAdvance_Title' | translateWF: 'Employee advances'">
              <app-trip-advances [tripReference]="currentId() ?? ''" />
            </p-panel>
            <p-panel [header]="'ExpenseSettlement_Title' | translateWF: 'Expense settlements'">
              <app-owner-settlements [ownerReference]="currentId() ?? ''" />
            </p-panel>
          }

          @if (!isNew()) {
            <p-panel [header]="'ExpenseClaim_Title' | translateWF: 'Expense claims'">
              <p-card>
                @if (isOpen() || status() === tripStatuses.InSettlement || status() === tripStatuses.Closed) {
                  <app-owner-claims [ownerReference]="currentId() ?? ''" />
                } @else {
                  <small class="text-surface-500">
                    {{ 'Trip_Money_Pending' | translateWF: 'Advances, receipts in the pool and expense claims will show here once the trip is open.' }}
                  </small>
                }
              </p-card>
            </p-panel>
          }
        </div>
      </div>
    }

    <p-dialog
      [visible]="decisionVisible()"
      (visibleChange)="decisionVisible.set($event)"
      [modal]="true"
      [style]="{ width: '32rem' }"
      [header]="isRejection() ? ('Trip_Reject_Title' | translateWF: 'Reject trip') : ('Trip_Approve_Title' | translateWF: 'Approve trip')"
    >
      <div class="form-box">
        <label for="trip-decision-comment">
          {{ 'Trip_Decision_Comment' | translateWF: 'Comment' }}
          @if (isRejection()) {
            *
          }
        </label>
        <textarea
          id="trip-decision-comment"
          pTextarea
          class="w-full"
          rows="3"
          maxlength="500"
          [formControl]="decisionComment"
          [ngClass]="{ 'ng-invalid ng-dirty': isRejection() && decisionComment.touched && !decisionComment.value.trim() }"
        ></textarea>
        @if (isRejection()) {
          <small class="text-surface-500">{{ 'Trip_Reject_Hint' | translateWF: 'Say why; the trip goes back to its creator, who can edit and resend it.' }}</small>
        }
      </div>
      <ng-template #footer>
        <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="outlined" (onClick)="decisionVisible.set(false)" />
        <p-button
          [label]="isRejection() ? ('Trip_Reject' | translateWF: 'Reject') : ('Trip_Approve' | translateWF: 'Approve')"
          [severity]="isRejection() ? 'danger' : 'success'"
          (onClick)="confirmDecision()"
        />
      </ng-template>
    </p-dialog>
  </div>
</main>
